'use client';
import { Button } from '@/components/shared/button';
import { Confetti } from '@/components/shared/confetti';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { twMerge } from 'tailwind-merge';

const WalkthroughContext = createContext();

export const useWalkthrough = (onComplete) => {
  const walkthrough = useContext(WalkthroughContext);
  walkthrough.setOnComplete(onComplete);
  return walkthrough.startWalkthrough;
};

const repeat = (n, fn) => {
  const arr = [];
  for (let i = 0; i < n; i++) {
    arr.push(fn(i));
  }
  return arr;
};

const walkthroughPositions = {
  'top-left': 'bottom-full mb-4 left-0',
  'top-right': 'bottom-full mb-4 right-0',
  'bottom-left': 'top-full mt-4 left-0',
  'bottom-right': 'top-full mt-4 right-0',
  'left-top':
    'bottom-full mb-4 lg:bottom-auto lg:mb-0 lg:right-full lg:mr-4 lg:top-0',
  'left-bottom':
    'top-full mt-4 lg:top-auto lg:bottom-0 lg:mt-0 md:right-full lg:mr-4 lg:bottom-0',
  'right-top':
    'bottom-full mb-4 lg:bottom-auto lg:mb-0 lg:left-full lg:ml-4 lg:top-0',
  'right-bottom':
    'top-full mt-4 lg:top-auto lg:bottom-0 lg:mt-0 lg:left-full lg:ml-4 lg:bottom-0'
};

export default function WalkthroughProvider({ children }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [currentElement, setCurrentElement] = useState(null);
  const [elementCount, setElementCount] = useState(0);
  const [walkthroughStage, setWalkthroughStage] = useState(null);
  const [isCompelte, setIsComplete] = useState(false);
  const overlayRef = useRef();

  const startWalkthrough = useCallback(() => {
    // Get walkthrough elements
    const elements = Array.from(
      document.querySelectorAll('[data-walkthrough-index]')
    );
    elements.sort(
      (a, b) =>
        parseInt(a.dataset.walkthroughIndex) -
        parseInt(b.dataset.walkthroughIndex)
    );

    // Make sure we have elements
    if (elements.length) {
      // Set the first element
      setCurrentElement(elements[0]);
      // Disable prev button
      setWalkthroughStage('first');
      // Set the dots count
      setElementCount(elements.length);
      // Close the sidebar, if it's open
      const sidebarToggle = document.getElementById('nav-toggle-check');
      if (sidebarToggle.checked) {
        sidebarToggle.checked = false;
      }
      // Reset the complete flag
      setIsComplete(false);
    }
  }, []);

  const nextItem = useCallback(() => {
    // Get walkthrough elements
    const elements = Array.from(
      document.querySelectorAll('[data-walkthrough-index]')
    );
    elements.sort(
      (a, b) =>
        parseInt(a.dataset.walkthroughIndex) -
        parseInt(b.dataset.walkthroughIndex)
    );
    // Get the next element in the list
    const nextElementIdx = elements.indexOf(currentElement) + 1;
    let nextElement = null;
    if (nextElementIdx < elements.length) {
      nextElement = elements[nextElementIdx];
      // Set the stage to "last" if we're on the last step, otherwise clear a potential "first step" flag
      const lastStep = nextElementIdx === elements.length - 1;
      setWalkthroughStage(lastStep ? 'last' : null);
    } else {
      // We're done! Run some congrats stuff.
      // Get doctor ID and move to "training & resources page"
      const doctorId = /\/partners\/(\d+)/.exec(window.location.href)?.[1];
      router.push(`/partners/${doctorId}/training-resources`);
      // Close the sidebar
      const sidebarToggle = document.getElementById('nav-toggle-check');
      if (sidebarToggle.checked) {
        sidebarToggle.checked = false;
      }
      // Set the complete flag (displays confetti) & reset the "last step" flag
      setIsComplete(true);
      setWalkthroughStage(null);
    }
    // Set the current element. Can be null & will end the walkthrough
    setCurrentElement(nextElement);
  }, [currentElement]);

  const prevItem = useCallback(() => {
    // Get walkthrough elements
    const elements = Array.from(
      document.querySelectorAll('[data-walkthrough-index]')
    );
    elements.sort(
      (a, b) =>
        parseInt(a.dataset.walkthroughIndex) -
        parseInt(b.dataset.walkthroughIndex)
    );
    // Get the previous element in the list
    const prevElementIdx = elements.indexOf(currentElement) - 1;
    let prevElement = null;
    if (prevElementIdx >= 0) {
      prevElement = elements[prevElementIdx];
      // Set the stage to "first" if we're on the first step, otherwise clear a potential "last step" flag
      const firstStep = elements.indexOf(prevElement) === 0;
      setWalkthroughStage(firstStep ? 'first' : null);
    }
    // Set the current element. Can be null & will end the walkthrough
    setCurrentElement(prevElement);
  }, [currentElement]);

  // Whenever the step changes (or the window is resized)
  useEffect(() => {
    // Debounce it to only run once we've "settled"
    let debounceVal;
    const resizeHandler = () => {
      if (currentElement) {
        if (debounceVal) {
          clearTimeout(debounceVal);
        }
        // SetTimeout to debounce repeated resize events
        debounceVal = setTimeout(async () => {
          // If it's in the sidebar, open the sidebar so we can see it
          const sidebarToggle = document.getElementById('nav-toggle-check');
          const sidebarDrawer = currentElement?.closest('.drawer-side');
          if (
            !sidebarToggle.checked &&
            sidebarDrawer?.children[1]?.getBoundingClientRect().left < 0
          ) {
            sidebarToggle.checked = true;
            // Wait for sidebar open
            await new Promise((resolve) => setTimeout(resolve, 400));
          } else if (!sidebarDrawer) {
            sidebarToggle.checked = false;
          }

          // Are we showing a "bottom" element under the current element
          const isBottom =
            currentElement.dataset.walkthroughPosition?.includes('bottom') ||
            false;
          const isRight =
            currentElement.dataset.walkthroughPosition?.includes('right') ||
            false;

          // Get the "text & heading" elements
          const contentElm = document.getElementsByClassName(
            'walkthrough-content'
          )[0];

          // Create in-tree positioning elements
          const positionElem = document.createElement('div');
          positionElem.className =
            'absolute bg-red-500 z-1000 opacity-50 overflow-hidden';
          currentElement.after(positionElem);
          positionElem.style.width = `${currentElement.offsetWidth}px`;
          positionElem.style.height = `${currentElement.offsetHeight}px`;
          positionElem.style.left = `${currentElement.offsetLeft}px`;
          positionElem.style.top = `${currentElement.offsetTop}px`;
          const pos = currentElement?.dataset.walkthroughPosition
            ? walkthroughPositions[currentElement?.dataset.walkthroughPosition]
            : walkthroughPositions['top-left'];
          const contentPositionElem = document.createElement('div');
          contentPositionElem.className =
            'absolute bg-blue-500 z-1000 opacity-50 ' + pos;
          contentPositionElem.style.width = `${contentElm.offsetWidth}px`;
          contentPositionElem.style.height = `${contentElm.offsetHeight + (isBottom ? 0 : 8)}px`;
          positionElem.appendChild(contentPositionElem);

          // Save all the scroll offsets
          const scrollOffsets = [];
          let currentParent = currentElement.parentElement;
          while (currentParent !== null) {
            scrollOffsets.push([
              currentParent.scrollLeft,
              currentParent.scrollTop
            ]);
            currentParent = currentParent.parentElement;
          }
          scrollOffsets.push([window.scrollX, window.scrollY]);

          // Scroll the positioning elements (instantly)
          contentPositionElem?.scrollIntoView({
            behavior: 'instant',
            block: isBottom ? 'end' : 'start',
            inline: isRight ? 'end' : 'start'
          });
          const rect = positionElem.getBoundingClientRect();

          // Get the position relative to the viewport
          let realPosition = {
            top: `${rect.top - 8}px`,
            left: `${rect.left - 8}px`,
            width: `${positionElem.offsetWidth + 16}px`,
            height: `${positionElem.offsetHeight + 16}px`
          };

          // Set the highlight over the current element
          if (overlayRef?.current) {
            overlayRef.current.style.width = realPosition.width;
            overlayRef.current.style.height = realPosition.height;
            overlayRef.current.style.top = realPosition.top;
            overlayRef.current.style.left = realPosition.left;
          }

          // Restore all scroll positions
          const windowScroll = scrollOffsets.pop();
          window.scroll({
            left: windowScroll[0],
            top: windowScroll[1],
            behavior: 'instant'
          });
          currentParent = currentElement.parentElement;

          while (currentParent !== null) {
            const scrollOffset = scrollOffsets.shift();
            currentParent.scrollLeft = scrollOffset[0];
            currentParent.scrollTop = scrollOffset[1];
            currentParent = currentParent.parentElement;
          }

          // Scroll again, but with animation
          contentPositionElem?.scrollIntoView({
            behavior: 'smooth',
            block: isBottom ? 'end' : 'start',
            inline: isRight ? 'end' : 'start'
          });
          // Remove the positioning elements
          positionElem.remove();
        }, 50);
      }
    };

    // Run this on resize to align things, or whenever the element changes
    window.addEventListener('resize', resizeHandler);
    if (currentElement) {
      resizeHandler();
    }

    // Clean up
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [currentElement, nextItem]);

  // If the URL has "tutorial" in it, start the tutorial!
  useEffect(() => {
    if (searchParams.get('tutorial')) {
      startWalkthrough();
      // Clear tutorial from URL
      setTimeout(() => {
        history.replaceState(
          null,
          '',
          window.location.href.replace(/(\?|&)tutorial=[^&]*&?/, '$1')
        );
      }, 100);
    }
  }, [searchParams]);

  // Figure out how to position the tutorial text
  const pos = currentElement?.dataset.walkthroughPosition
    ? walkthroughPositions[currentElement?.dataset.walkthroughPosition]
    : walkthroughPositions['top-left'];

  return (
    <WalkthroughContext.Provider
      value={{
        startWalkthrough,
        setOnComplete: (fn) => (onCompleteRef.current = fn)
      }}
    >
      {children}
      {isCompelte && <Confetti pieces={500} />}
      <div
        className={twMerge(
          'opacity-0 pointer-events-none fixed shadow-[0_0_0_1000vmax_rgba(0,0,0,0.5)] z-1000 rounded-lg transition-all',
          currentElement &&
            'opacity-100 pointer-events-auto border-4 border-primary '
        )}
        ref={overlayRef}
      >
        {currentElement?.dataset.walkthroughTitle &&
          currentElement?.dataset.walkthroughContent && (
            <div
              className={twMerge(
                'absolute rounded-2xl border border-white bg-white/80 backdrop-blur-sm p-6 w-max max-w-[calc(100vw-(var(--spacing)*10))] md:max-w-150 walkthrough-content',
                pos
              )}
            >
              <h3 className="text-base font-bold mb-2">
                {currentElement.dataset.walkthroughTitle}
              </h3>
              <p>{currentElement.dataset.walkthroughContent}</p>
              <div className="grid gap-x-4 gap-y-2 grid-cols-1 md:grid-cols-2 mt-4 md:mt-2 items-center">
                <div className="flex mr-4 w-full justify-center md:justify-start">
                  {repeat(elementCount, (i) => (
                    <button
                      key={i}
                      className={twMerge(
                        'w-3 h-3 rounded-full mr-1 bg-base-300 has-[~_.current]:bg-beautifi-blue-dark',
                        parseInt(currentElement?.dataset.walkthroughIndex) ===
                          i && 'bg-beautifi-blue-dark current'
                      )}
                    ></button>
                  ))}
                </div>
                <div className="flex md:justify-end">
                  <Button
                    className={twMerge(
                      'bg-beautifi-blue-dark btn-sm text-white w-24 ml-auto flex-1',
                      walkthroughStage === 'first' && 'opacity-50'
                    )}
                    onClick={() => prevItem()}
                    disabled={walkthroughStage === 'first'}
                  >
                    Previous
                  </Button>
                  <Button
                    className="bg-beautifi-blue-dark btn-sm text-white w-24 ml-2 flex-1"
                    onClick={() => nextItem()}
                  >
                    {walkthroughStage !== 'last' && 'Next'}
                    {walkthroughStage === 'last' && 'Finish'}
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>
    </WalkthroughContext.Provider>
  );
}
